import classNames from 'classnames';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import styles from './SelectPhoneNumberForm.module.css';
import { LoadingSpinner } from '../../../../ui/loadingSpinner';
import {
    SEND_VERIFICATION_SMS,
    TOGGLE_ADD_PHONE_NUMBER
} from '../../IdCheckVerificationModal.actions';
import { TDeserialisedPerson } from '../../IdCheckVerificationModal.types';
import { SelectPhoneNumber } from '../../selectPhoneNumber/SelectPhoneNumber';

const mapDispatchToProps = (dispatch: Dispatch) => ({
    sendSMS: (person: TDeserialisedPerson, phoneNumber: string) =>
        dispatch({ type: SEND_VERIFICATION_SMS, person, phoneNumber }),
    toggleAddPhoneNumber: (personUID: string) =>
        dispatch({ type: TOGGLE_ADD_PHONE_NUMBER, personUID })
});

type PropsFromRedux = ReturnType<typeof mapDispatchToProps>;

type TComponentProps = {
    person: TDeserialisedPerson;
};

type TProps = PropsFromRedux & TComponentProps;

type TFormData = {
    [uid: string]: string;
};

const SelectPhoneNumberFormComponent = ({
    person,
    sendSMS,
    toggleAddPhoneNumber
}: TProps) => {
    const { control, watch, handleSubmit } = useForm<TFormData>({
        defaultValues: { [person.uid]: '' }
    });

    const selectedNumber = watch(person.uid);

    const handleFormSubmit = (values: TFormData) => {
        sendSMS(person, values[person.uid]);
    };

    const isVerified = person.idCheckStatus === 'Verified';

    const shouldDisablePhoneInput =
        isVerified || person.reachedMaxIdCheckAttempts;

    const shouldDisableSubmitButton =
        !selectedNumber ||
        person.SMSPending ||
        isVerified ||
        person.reachedMaxIdCheckAttempts;

    return (
        <form
            onSubmit={handleSubmit(handleFormSubmit)}
            className={styles.SelectPhoneNumberForm}
        >
            <div className={styles.SelectPhoneNumberFormWrapper}>
                <Controller
                    control={control}
                    name={person.uid}
                    render={({ field: { onChange, value } }) => (
                        <SelectPhoneNumber
                            value={value}
                            onChange={onChange}
                            options={person.phones}
                            disabled={shouldDisablePhoneInput}
                            addNewNumber={() =>
                                toggleAddPhoneNumber(person.uid)
                            }
                        />
                    )}
                />

                <button
                    type="submit"
                    disabled={shouldDisableSubmitButton}
                    className={classNames(
                        styles.sendSMS,
                        !selectedNumber && styles.sendSMSDisabled
                    )}
                >
                    {renderButtonMessage(person)}
                </button>
            </div>
        </form>
    );
};

const renderButtonMessage = (person: TDeserialisedPerson) => {
    if (person.SMSPending) {
        return (
            <LoadingSpinner
                data-testid="onfido-loading-spinner"
                className={styles.loading}
            />
        );
    } else if (person.idCheckStatus.includes('Verifying')) {
        return (
            <FormattedMessage
                id="_.modal.id_check_verification.resend_message"
                defaultMessage="Re-send SMS"
            />
        );
    }

    return (
        <FormattedMessage
            id="_.modal.id_check_verification.send"
            defaultMessage="Send SMS"
        />
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(
    null,
    mapDispatchToProps
)(SelectPhoneNumberFormComponent);
